import React, { useState } from 'react';
import { labelsForMode, MODES } from '../lib/opportunityMode';
import {
  ANALYSIS_TABS,
  DEFAULT_ANALYSIS_TAB,
  getMarketplaceBlock,
  hasMarketplaceTab,
  readinessPillLabel,
  humaniseDimensionKey,
  formatMarketplaceRecommendation,
} from '../lib/analysisTabs';
import {
  commercialPriorityLabel,
  commercialPriorityClass,
  riskPillClass,
  riskPillLabel,
  selectHeadline,
} from '../lib/commercialPriority';

function getScoreClass(score, inverted = false) {
  const adjusted = inverted ? 100 - score : score;
  if (adjusted >= 70) return 'high';
  if (adjusted >= 40) return 'medium';
  return 'low';
}

function getRiskClass(score) {
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
}

function formatRecommendation(rec) {
  return (rec || '').replace(/_/g, ' ').toUpperCase();
}

export default function AnalysisResults({ analysis, onReset, onGenerateRoadmap, onDownloadPDF, onDownloadPNG, loading }) {
  // Iter 35b — Two-tab UI. Consultancy View is the protected default
  // and always renders the legacy consultancy layout. Marketplace
  // Opportunity tab appears only when the backend attached the
  // Iter-35a additive `marketplace_opportunity` block.
  const [activeTab, setActiveTab] = useState(DEFAULT_ANALYSIS_TAB);
  const showMarketplaceTab = hasMarketplaceTab(analysis);
  // Defensive: if the active tab is marketplace but the block went
  // away (e.g. analysis re-fetched as a legacy doc), fall back to
  // the consultancy tab so the screen never renders empty.
  const resolvedTab = (activeTab === ANALYSIS_TABS.MARKETPLACE && showMarketplaceTab)
    ? ANALYSIS_TABS.MARKETPLACE
    : ANALYSIS_TABS.CONSULTANCY;

  if (!analysis || !analysis.scores) {
    return (
      <div className="panel" data-testid="analysis-results-incomplete">
        <div className="panel-body" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          This snapshot doesn't include a full scored analysis. The saved investigation may have been
          created before an Analyse step completed, or stored only high-level metadata.
        </div>
      </div>
    );
  }

  return (
    <div>
      {showMarketplaceTab && (
        <AnalysisTabBar
          activeTab={resolvedTab}
          onChange={setActiveTab}
        />
      )}
      {resolvedTab === ANALYSIS_TABS.CONSULTANCY ? (
        <ConsultancyView
          analysis={analysis}
          onReset={onReset}
          onGenerateRoadmap={onGenerateRoadmap}
          onDownloadPDF={onDownloadPDF}
          onDownloadPNG={onDownloadPNG}
          loading={loading}
        />
      ) : (
        <MarketplaceView
          analysis={analysis}
          onReset={onReset}
          onGenerateRoadmap={onGenerateRoadmap}
          onDownloadPDF={onDownloadPDF}
          onDownloadPNG={onDownloadPNG}
          loading={loading}
        />
      )}
    </div>
  );
}


// ── Tab bar ──────────────────────────────────────────────────────────

function AnalysisTabBar({ activeTab, onChange }) {
  return (
    <div
      className="panel"
      style={{ marginBottom: '8px', padding: 0 }}
      data-testid="analysis-tabs"
    >
      <div
        style={{
          display: 'flex',
          gap: 0,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <TabButton
          label="Consultancy View"
          active={activeTab === ANALYSIS_TABS.CONSULTANCY}
          onClick={() => onChange(ANALYSIS_TABS.CONSULTANCY)}
          testId="analysis-tab-consultancy"
        />
        <TabButton
          label="Marketplace Opportunity"
          active={activeTab === ANALYSIS_TABS.MARKETPLACE}
          onClick={() => onChange(ANALYSIS_TABS.MARKETPLACE)}
          testId="analysis-tab-marketplace"
        />
      </div>
    </div>
  );
}

function TabButton({ label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-selected={active}
      role="tab"
      style={{
        appearance: 'none',
        background: 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--primary)' : '2px solid transparent',
        color: active ? 'var(--primary)' : 'var(--text-secondary)',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: active ? 600 : 500,
        letterSpacing: '0.02em',
        padding: '10px 16px',
        marginBottom: '-1px',
      }}
    >
      {label}
    </button>
  );
}


// ── Consultancy View (legacy layout, restored as default) ───────────

function ConsultancyView({ analysis, onReset, onGenerateRoadmap, onDownloadPDF, onDownloadPNG, loading }) {
  const {
    scores, detected_patterns, executive_summary, recommendation,
    signal_count, company_name, vector, sub_vector,
    verification_status, evidence_profile, analysis_lens,
  } = analysis;
  // Iter 35b — Consultancy View *always* uses consultancy labels and
  // consultancy scoring regardless of the persisted `opportunity_mode`.
  // The mode-aware Iter 34 view is no longer the default; it now
  // lives behind the Marketplace tab.
  const labels = labelsForMode(MODES.CONSULTANCY);
  // Iter 38a — opportunity-led headline. `selectHeadline` returns the
  // Commercial Priority composite for re-scored analyses and falls
  // back to the legacy `overall_risk_score`/`recommendation` headline
  // for old saved data so legacy analyses render safely.
  const headline = selectHeadline(scores, recommendation, labels.analyseScoreLabel);
  const riskScore = scores.overall_risk_score;
  const cpScore = headline.mode === 'commercial_priority' ? headline.score : null;
  const cpReason = headline.reason || '';
  const headlineScore = headline.score;
  const headlineLabel = headline.label;
  const headlineClass = headline.headlineClass;
  const headlineLabelText = headline.headlineLabelText;
  const modeRecommendation = recommendation;
  const riskClass = getRiskClass(riskScore);
  const scopeLabel = [company_name, vector, sub_vector].filter(Boolean).join(' > ');
  const dims = scores.dimension_scores || {};
  const vs = verification_status || evidence_profile?.verification_status || '';
  const ep = evidence_profile || {};
  const lensLabels = {
    commercial_opportunity: 'Commercial Opportunity',
    delivery_risk: 'Delivery Risk',
    operational_risk: 'Operational Risk',
    transformation_pressure: 'Transformation Pressure',
    platform_health: 'Platform Health',
    optimisation: 'Optimisation',
  };
  const lensLabel = analysis_lens ? lensLabels[analysis_lens] || analysis_lens : null;
  const metaLens = analysis.metadata?.analysis_lens;
  const displayLens = lensLabel || (metaLens ? (lensLabels[metaLens] || metaLens) : null);

  return (
    <div data-testid="analysis-consultancy-view">
      {vs && (
        <div className="panel" style={{ marginBottom: '4px', borderLeft: `3px solid ${
          vs === 'verified' ? 'var(--success)' :
          vs === 'corroborated' ? 'var(--primary)' :
          vs === 'partial' ? 'var(--warning)' :
          'var(--danger)'
        }` }}>
          <div className="panel-body" style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }} data-testid="analysis-verification-bar">
            <span className={`badge ${
              vs === 'verified' ? 'badge-high' :
              vs === 'corroborated' ? 'badge-medium' :
              'badge-low'
            }`} style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}
              data-testid="analysis-verification-status"
            >
              {vs.replace(/_/g, ' ')}
            </span>
            {ep.source_count !== undefined && (
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }} data-testid="evidence-source-count">
                {ep.source_count} source{ep.source_count !== 1 ? 's' : ''}
              </span>
            )}
            {ep.type_count !== undefined && (
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }} data-testid="evidence-type-count">
                {ep.type_count} signal type{ep.type_count !== 1 ? 's' : ''}
              </span>
            )}
            {ep.corroboration_score !== undefined && (
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }} data-testid="evidence-corroboration">
                Corroboration: {Math.round(ep.corroboration_score * 100)}%
              </span>
            )}
            {ep.source_diversity_score !== undefined && (
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }} data-testid="evidence-diversity">
                Diversity: {Math.round(ep.source_diversity_score * 100)}%
              </span>
            )}
          </div>
        </div>
      )}

      <div className={`risk-score-display ${headlineClass}`} data-testid="overall-risk-display">
        <div className="risk-number" data-testid="commercial-priority-score">
          {headlineScore}
        </div>
        <div>
          <div className="risk-label" data-testid="analysis-score-label">{headlineLabelText}</div>
          <div
            className="risk-recommendation"
            data-testid="commercial-priority-label"
            style={{ color: headlineClass === 'high' ? 'var(--success)' : headlineClass === 'medium' ? 'var(--warning)' : 'var(--text-secondary)' }}
          >
            {headlineLabel}
          </div>
          {/* Iter 38a — Risk reading kept visible as a secondary pill. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
            <span
              className={`badge ${riskPillClass(riskScore)}`}
              style={{ fontSize: '10px', letterSpacing: '0.04em' }}
              data-testid="overall-risk-pill"
              title={`Overall Risk Score: ${riskScore}/100. Legacy recommendation: ${formatRecommendation(modeRecommendation)}.`}
            >
              {riskPillLabel(riskScore)} · <span data-testid="overall-risk-score">{riskScore}</span>/100
            </span>
            {cpReason && (
              <span
                style={{ fontSize: '11px', color: 'var(--text-secondary)' }}
                data-testid="commercial-priority-reason"
                title={cpReason}
              >
                {cpReason}
              </span>
            )}
          </div>
          {/* Iter 38a — Legacy `recommendation-level` testid preserved
              for backward compatibility with PDF exports, screenshots,
              testing-agent e2e and saved-data analytics consumers. */}
          <span
            data-testid="recommendation-level"
            style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
            aria-hidden="true"
          >
            {formatRecommendation(modeRecommendation)}
          </span>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            {scopeLabel} &middot; {signal_count} signals analyzed
            {displayLens && (
              <span className="badge badge-type" style={{ marginLeft: '8px', fontSize: '10px' }} data-testid="analysis-lens-badge">
                {displayLens}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: '0' }}>
        <div className="panel-body" style={{ padding: '8px 16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <CompositeScore label="Opportunity" value={scores.opportunity_score} testId="score-opportunity" tooltip="How strong the commercial or engagement opportunity appears based on change signals and risk profile" />
          <CompositeScore label="Change Strength" value={scores.change_signal_strength} testId="score-change-strength" tooltip="Intensity of active change signals — transformation, M&A, leadership shifts, vendor changes" />
          <CompositeScore label="Intervention" value={scores.intervention_likelihood} testId="score-intervention" tooltip="How likely the organisation appears to need or accept external support" />
          <CompositeScore label="Evidence Quality" value={scores.evidence_quality_score} testId="score-evidence-quality" tooltip="Strength and diversity of the underlying evidence — higher means more sources and corroboration" />
          {scores.conservative_mode && (
            <span style={{ fontSize: '11px', color: 'var(--warning)', alignSelf: 'center', fontStyle: 'italic' }} data-testid="conservative-mode">
              Conservative mode
            </span>
          )}
        </div>
      </div>

      <div className="analysis-grid">
        <div>
          <div className="panel">
            <div className="panel-header" title="Each dimension is weighted by strategic importance — higher weights mean more influence on composite scores">
              Weighted Dimensions
            </div>
            <div className="panel-body" style={{ padding: '8px' }}>
              <div className="score-grid" style={{ gridTemplateColumns: '1fr' }}>
                {Object.entries(dims).map(([key, dim]) => (
                  <ScoreRow key={key} label={dim.label} value={dim.score} testId={`dim-${key}`} />
                ))}
              </div>
            </div>
          </div>
          {scores.score_drivers && scores.score_drivers.length > 0 && (
            <div className="panel" style={{ marginTop: '4px' }}>
              <div className="panel-header" title="Key factors driving the composite scores — what the evidence points to most strongly">Score Drivers</div>
              <div className="panel-body" style={{ padding: '8px 12px' }}>
                {scores.score_drivers.map((d, i) => (
                  <div key={i} style={{ fontSize: '12px', padding: '3px 0', color: 'var(--text-secondary)' }}>
                    &bull; {d}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="panel">
            <div className="panel-header" title="Correlated signal patterns — when multiple signal types co-occur, they may indicate a systemic theme">
              Detected Patterns
              <span className="badge badge-type" data-testid="pattern-count">{detected_patterns.length}</span>
            </div>
            <div className="panel-body" style={{ padding: '8px' }}>
              {detected_patterns.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  No stress patterns detected
                </div>
              ) : (
                detected_patterns.map((p, i) => (
                  <div key={i} className="pattern-item" data-testid={`pattern-${i}`}>
                    <div className="pattern-name">{p.pattern_name}</div>
                    <div className="pattern-confidence" style={{ color: p.confidence >= 0.75 ? 'var(--danger)' : 'var(--warning)' }}>
                      {Math.round(p.confidence * 100)}% confidence &middot; {p.contributing_signals.length} signals
                    </div>
                    <div className="pattern-desc">{p.description}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginTop: '12px' }}>
        <div className="panel-header">Executive Summary</div>
        <div className="panel-body">
          <div className="executive-summary" data-testid="executive-summary">
            {executive_summary}
          </div>
        </div>
      </div>

      <ActionBar
        ctaLabel={labels.analyseCta}
        ctaLoadingLabel={labels.analyseCtaLoading}
        loading={loading}
        onGenerateRoadmap={onGenerateRoadmap}
        onDownloadPDF={onDownloadPDF}
        onDownloadPNG={onDownloadPNG}
        onReset={onReset}
      />
    </div>
  );
}


// ── Marketplace Opportunity View (Iter 35a additive block) ──────────

function MarketplaceView({ analysis, onReset, onGenerateRoadmap, onDownloadPDF, onDownloadPNG, loading }) {
  const block = getMarketplaceBlock(analysis);
  // The tab is only rendered when the block exists; this guard exists
  // for defensive safety only.
  if (!block) return null;

  const {
    sector, roles, readiness_mode, score, recommendation,
    approach_shape, dimensions, next_steps, disqualifiers,
    evidence_summary,
  } = block;
  const { company_name, vector, sub_vector } = analysis;
  const scopeLabel = [company_name, vector, sub_vector].filter(Boolean).join(' > ');
  const compositeScore = typeof score === 'number' ? score : 0;
  const riskClass = getRiskClass(compositeScore);
  const pill = readinessPillLabel(readiness_mode);
  // CTA label follows the readiness mode so the operator gets the
  // mode-native call to action (e.g. "Generate Opportunity Plan" for
  // purchase readiness). Generation behaviour itself is unchanged.
  const ctaLabels = labelsForMode(readiness_mode || MODES.CONSULTANCY);

  return (
    <div data-testid="analysis-marketplace-view">
      <div className={`risk-score-display ${riskClass}`} data-testid="marketplace-score-display">
        <div className="risk-number" data-testid="marketplace-score">
          {compositeScore}
        </div>
        <div>
          <div className="risk-label" data-testid="marketplace-score-label">
            Marketplace Opportunity / 100
          </div>
          <div
            className="risk-recommendation"
            data-testid="marketplace-recommendation"
            style={{ color: riskClass === 'high' ? 'var(--danger)' : riskClass === 'medium' ? 'var(--warning)' : 'var(--success)' }}
          >
            {formatMarketplaceRecommendation(recommendation)}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px' }}>
            {scopeLabel}
            {pill && (
              <span
                className="badge badge-type"
                style={{ marginLeft: '8px', fontSize: '10px' }}
                data-testid="marketplace-mode-pill"
              >
                {pill}
              </span>
            )}
            {sector && (
              <span
                className="badge badge-type"
                style={{ marginLeft: '8px', fontSize: '10px' }}
                data-testid="marketplace-sector-badge"
              >
                {sector}
              </span>
            )}
          </div>
        </div>
      </div>

      {roles && roles.length > 0 && (
        <div className="panel" style={{ marginBottom: '8px' }} data-testid="marketplace-roles-panel">
          <div className="panel-header" title="Marketplace participant roles registered for this sector">
            Marketplace Roles
          </div>
          <div className="panel-body" style={{ padding: '10px 14px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {roles.map((role, i) => (
              <span
                key={`${role}-${i}`}
                className="badge badge-type"
                style={{ fontSize: '11px', textTransform: 'capitalize' }}
                data-testid={`marketplace-role-${i}`}
              >
                {role}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="analysis-grid">
        <div>
          <div className="panel">
            <div className="panel-header" title="Per-dimension readiness scores driving the composite marketplace score">
              Readiness Dimensions
            </div>
            <div className="panel-body" style={{ padding: '8px' }}>
              <div className="score-grid" style={{ gridTemplateColumns: '1fr' }}>
                {(dimensions || []).map((d, i) => (
                  <ScoreRow
                    key={d.key || i}
                    label={humaniseDimensionKey(d.key)}
                    value={d.score || 0}
                    testId={`marketplace-dim-${d.key}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div>
          {approach_shape && (
            <div className="panel" data-testid="marketplace-approach-shape">
              <div className="panel-header" title="Best-fit operator approach derived from the strongest readiness driver">
                Approach Shape
              </div>
              <div className="panel-body" style={{ padding: '10px 14px', fontSize: '13px' }}>
                {approach_shape}
              </div>
            </div>
          )}
          {next_steps && next_steps.length > 0 && (
            <div className="panel" style={{ marginTop: '4px' }} data-testid="marketplace-next-steps">
              <div className="panel-header" title="Suggested next moves the operator can act on now">
                Next Steps
              </div>
              <div className="panel-body" style={{ padding: '8px 14px' }}>
                {next_steps.map((s, i) => (
                  <div key={i} style={{ fontSize: '12px', padding: '3px 0' }} data-testid={`marketplace-next-step-${i}`}>
                    &bull; {s}
                  </div>
                ))}
              </div>
            </div>
          )}
          {disqualifiers && disqualifiers.length > 0 && (
            <div className="panel" style={{ marginTop: '4px' }} data-testid="marketplace-disqualifiers">
              <div className="panel-header" title="Reasons to verify or hold before approaching the target">
                Watch-outs
              </div>
              <div className="panel-body" style={{ padding: '8px 14px' }}>
                {disqualifiers.map((d, i) => (
                  <div
                    key={i}
                    style={{ fontSize: '12px', padding: '3px 0', color: 'var(--warning)' }}
                    data-testid={`marketplace-disqualifier-${i}`}
                  >
                    &bull; {d}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {evidence_summary && (
        <div className="panel" style={{ marginTop: '12px' }}>
          <div className="panel-header">Evidence Summary</div>
          <div className="panel-body">
            <div className="executive-summary" data-testid="marketplace-evidence-summary">
              {evidence_summary}
            </div>
          </div>
        </div>
      )}

      <ActionBar
        ctaLabel={ctaLabels.analyseCta}
        ctaLoadingLabel={ctaLabels.analyseCtaLoading}
        loading={loading}
        onGenerateRoadmap={onGenerateRoadmap}
        onDownloadPDF={onDownloadPDF}
        onDownloadPNG={onDownloadPNG}
        onReset={onReset}
      />
    </div>
  );
}


// ── Shared bits ──────────────────────────────────────────────────────

function ActionBar({ ctaLabel, ctaLoadingLabel, loading, onGenerateRoadmap, onDownloadPDF, onDownloadPNG, onReset }) {
  return (
    <div style={{ paddingTop: '12px', display: 'flex', gap: '8px' }}>
      <button
        className="btn btn-primary"
        onClick={onGenerateRoadmap}
        disabled={loading}
        data-testid="generate-roadmap-button"
      >
        {loading ? ctaLoadingLabel : ctaLabel}
      </button>
      {onDownloadPDF && (
        <button
          className="btn btn-outline"
          onClick={onDownloadPDF}
          data-testid="download-analysis-pdf-button"
        >
          Download PDF
        </button>
      )}
      {onDownloadPNG && (
        <button
          className="btn btn-outline"
          onClick={onDownloadPNG}
          data-testid="download-analysis-png-button"
        >
          Export as PNG
        </button>
      )}
      <button
        className="btn btn-outline"
        onClick={onReset}
        data-testid="new-scan-button"
      >
        New Scan
      </button>
    </div>
  );
}

function ScoreRow({ label, value, inverted = false, testId }) {
  const cls = getScoreClass(value, inverted);
  return (
    <div className="score-item" data-testid={testId}>
      <span className="score-label">{label}</span>
      <span className={`score-value ${cls}`}>{value}</span>
    </div>
  );
}

function CompositeScore({ label, value, testId, tooltip }) {
  const cls = value >= 70 ? 'high' : value >= 40 ? 'medium' : 'low';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} data-testid={testId}>
      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', cursor: tooltip ? 'help' : 'default', borderBottom: tooltip ? '1px dotted var(--text-secondary)' : 'none' }} title={tooltip}>{label}</span>
      <span className={`score-value ${cls}`} style={{ fontSize: '14px', fontWeight: '700' }}>{value}</span>
    </div>
  );
}
